import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  type DialogProps,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import type {DosageDetails, MedicationForm} from '@/services/graphqlHooks';

import {
  FORM_SUGGESTED_UNITS,
  mapResidentMedicationDosageFormValuesToDosageInput,
  residentMedicationDosageFormDefaultValues,
  residentMedicationDosageFormSchema,
  type ResidentMedicationDosageFormValues,
} from '../utils/medicationFormInputs';
import type {DosageDetailsWithId} from '../utils/medicationTypes';
import ResidentMedicationDosageFormFields from './ResidentMedicationDosageFormFields';

export type DosageDialogProps = DialogProps & {
  dosageDetails?: DosageDetailsWithId;
  selectedMedicationForm?: MedicationForm;
  onSubmit?: (dosage: DosageDetails) => void;
};

const DosageDialog = ({
  dosageDetails,
  selectedMedicationForm,
  onOpenChange,
  onSubmit,
  ...props
}: DosageDialogProps) => {
  const {t} = useTranslation();

  const suggestedUnit = selectedMedicationForm
    ? FORM_SUGGESTED_UNITS[selectedMedicationForm]?.[0]
    : undefined;

  const form = useForm<ResidentMedicationDosageFormValues>({
    resolver: zodResolver(residentMedicationDosageFormSchema),
    mode: 'onBlur',
    defaultValues: residentMedicationDosageFormDefaultValues(
      dosageDetails,
      suggestedUnit
    ),
  });

  const handleSubmit = (values: ResidentMedicationDosageFormValues) => {
    onSubmit?.(mapResidentMedicationDosageFormValuesToDosageInput(values));
    handleClose(true);
  };

  const handleClose = (value: boolean) => {
    form.reset();
    onOpenChange?.(value);
  };

  useEffect(() => {
    if (!dosageDetails?.id) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {id, ...dosage} = dosageDetails;
    form.reset(
      residentMedicationDosageFormDefaultValues(dosage, suggestedUnit)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dosageDetails?.id]);

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Dosage')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              <ResidentMedicationDosageFormFields
                form={form}
                selectedMedicationForm={selectedMedicationForm}
              />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  handleClose(false);
                }}
              >
                {t('Cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default DosageDialog;
